import { Autocomplete, TextField } from "@mui/material";

import { substringFilter } from "@/shared/text/substringFilter";

import styles from "./SearchSelect.module.css";

export interface SearchSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Single-select combobox: a free-text input that filters `options` to the
 * case-insensitive substring matches of the typed text, earliest match first.
 * The committed value is always one of `options` — typing only narrows the
 * suggestions. When the field still shows the committed value untouched, the
 * full list is offered so the user can browse rather than only seeing matches
 * of the current value.
 */
export function SearchSelect({
  label,
  value,
  options,
  onChange,
  className,
}: SearchSelectProps) {
  return (
    <Autocomplete
      className={className}
      size="small"
      autoHighlight
      disableClearable
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next)}
      filterOptions={(opts, state) =>
        state.inputValue === value
          ? opts
          : substringFilter(state.inputValue, opts)
      }
      slotProps={{
        popper: {
          className: styles.popper,
          // Keep the (content-wide) dropdown anchored to the input's left edge:
          // bottom-start, and disable start↔end variation flipping so it grows
          // rightward instead of right-aligning when it would overflow.
          placement: "bottom-start",
          modifiers: [{ name: "flip", options: { flipVariations: false } }],
        },
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
